// Small shared bits — port of design/v3/AnalyticsBits.jsx.

import type { CSSProperties, ReactNode } from 'react';

interface DeltaProps {
  curr: number;
  prev: number;
  inverse?: boolean;
  label?: string;
}

export function Delta({ curr, prev, inverse = false, label }: DeltaProps) {
  if (!prev) return null;
  const d = ((curr - prev) / prev) * 100;
  const positive = inverse ? d < 0 : d >= 0;
  const color = positive ? 'var(--success)' : 'var(--danger)';
  const bg = positive ? 'var(--success-bg)' : 'var(--danger-bg)';
  const arrow = d >= 0 ? '▲' : '▼';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '2px 7px',
        borderRadius: 4,
        background: bg,
        color,
        fontSize: 12,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 9, lineHeight: 1 }}>{arrow}</span>
      {Math.abs(d).toFixed(1)}%
      {label && (
        <span style={{ color: 'var(--fg-3)', fontWeight: 400, marginLeft: 3 }}>
          {label}
        </span>
      )}
    </span>
  );
}

interface ShareBarProps {
  value: number;
  max: number;
  color?: string;
  height?: number;
}

export function ShareBar({
  value,
  max,
  color = 'var(--primary-strong)',
  height = 6,
}: ShareBarProps) {
  const pct = Math.max(2, max ? (value / max) * 100 : 0);
  return (
    <div
      style={{
        width: '100%',
        height,
        background: 'var(--bg-muted)',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 3,
          transition: 'width 280ms var(--ease-out)',
        }}
      />
    </div>
  );
}

interface MiniBarsProps {
  data: number[];
  height?: number;
  color?: string;
  onHover?: (i: number | null) => void;
  activeIndex?: number | null;
  tint?: boolean;
}

export function MiniBars({
  data,
  height = 36,
  color = 'var(--primary-strong)',
  onHover,
  activeIndex,
  tint = true,
}: MiniBarsProps) {
  const max = Math.max(0, ...data);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        height,
        width: '100%',
      }}
    >
      {data.map((v, i) => {
        const h = max ? (v / max) * height : 0;
        const active = activeIndex === i;
        return (
          <div
            key={i}
            onMouseEnter={() => onHover?.(i)}
            onMouseLeave={() => onHover?.(null)}
            style={{
              flex: 1,
              minWidth: 3,
              height: h || 2,
              background: active
                ? 'var(--primary-strong)'
                : tint && v === max
                  ? color
                  : 'var(--primary)',
              borderRadius: '2px 2px 0 0',
              transition:
                'background 120ms, height 280ms var(--ease-out)',
              cursor: onHover ? 'pointer' : 'default',
              opacity: active || activeIndex == null ? 1 : 0.7,
            }}
          />
        );
      })}
    </div>
  );
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}

export function Sparkline({
  data,
  width = 200,
  height = 40,
  color = 'var(--primary-strong)',
}: SparklineProps) {
  if (!data.length)
    return <svg width={width} height={height} aria-hidden />;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1 || 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={`0,${height} ${pts} ${width},${height}`}
        fill={color}
        opacity={0.08}
      />
    </svg>
  );
}

interface HeatCellProps {
  value: number;
  vmax?: number;
  vmin?: number;
}

export function HeatCell({ value, vmax = 50, vmin = 20 }: HeatCellProps) {
  const t = Math.max(0, Math.min(1, (value - vmin) / (vmax - vmin)));
  const bg =
    t < 0.5
      ? `rgba(253, 186, 116, ${0.12 + t * 0.6})`
      : `rgba(194, 65, 12, ${0.25 + (t - 0.5) * 1.2})`;
  const color = t > 0.6 ? '#fff' : 'var(--fg-1)';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        minWidth: 52,
        textAlign: 'center',
        borderRadius: 4,
        background: bg,
        color,
        fontWeight: 600,
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toFixed(1)}%
    </span>
  );
}

interface SectionHeadProps {
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  tight?: boolean;
}

export function SectionHead({ title, sub, right, tight }: SectionHeadProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: tight ? 12 : 16,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {sub && (
          <span
            style={{
              fontSize: 12,
              color: 'var(--fg-3)',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </span>
        )}
      </div>
      {right && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          {right}
        </div>
      )}
    </div>
  );
}

interface SortCaretProps {
  dir: 'asc' | 'desc' | null | undefined;
}

export function SortCaret({ dir }: SortCaretProps) {
  if (!dir)
    return (
      <span style={{ opacity: 0.3, fontSize: 10, marginLeft: 3 }}>↕</span>
    );
  return (
    <span
      style={{
        color: 'var(--primary-strong)',
        fontSize: 11,
        marginLeft: 3,
        fontWeight: 700,
      }}
    >
      {dir === 'asc' ? '▲' : '▼'}
    </span>
  );
}

interface KPITileProps {
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  prev?: number | null;
  curr?: number;
  inverse?: boolean;
  compact?: boolean;
  accent?: string;
  style?: CSSProperties;
}

export function KPITile({
  label,
  value,
  unit,
  sub,
  prev,
  curr,
  inverse,
  compact,
  accent,
  style,
}: KPITileProps) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-lg)',
        padding: compact ? '12px 14px' : '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {accent && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            background: accent,
            opacity: 0.5,
          }}
        />
      )}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
          whiteSpace: 'nowrap',
        }}
      >
        <span
          style={{
            fontSize: compact ? 20 : 26,
            fontWeight: 700,
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {value}
        </span>
        {unit && (
          <span
            style={{
              fontSize: compact ? 12 : 14,
              fontWeight: 500,
              color: 'var(--fg-3)',
            }}
          >
            {unit}
          </span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 2,
          flexWrap: 'wrap',
        }}
      >
        {prev != null && curr != null && prev !== 0 && (
          <Delta curr={curr} prev={prev} inverse={inverse} />
        )}
        {sub && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {sub}
          </span>
        )}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  title?: string;
  message?: string;
  action?: ReactNode;
}

export function EmptyState({
  title = '데이터가 없습니다',
  message = '데이터가 누적되면 표시됩니다.',
  action,
}: EmptyStateProps) {
  return (
    <div
      style={{
        padding: '24px 16px',
        textAlign: 'center',
        color: 'var(--fg-3)',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--fg-2)' }}>{title}</div>
      <div>{message}</div>
      {action}
    </div>
  );
}

interface ErrorStateProps {
  message?: string;
}

export function ErrorState({
  message = '데이터를 불러오지 못했습니다.',
}: ErrorStateProps) {
  return (
    <div
      style={{
        padding: '20px 16px',
        textAlign: 'center',
        color: 'var(--danger)',
        fontSize: 13,
        background: 'var(--danger-bg)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--danger-100)',
      }}
    >
      {message}
    </div>
  );
}

export function LoadingState({ label = '불러오는 중…' }: { label?: string }) {
  return (
    <div
      style={{
        padding: '20px 16px',
        textAlign: 'center',
        color: 'var(--fg-3)',
        fontSize: 13,
        animation: 'dvvb-pulse 1.4s ease-in-out infinite',
      }}
    >
      {label}
    </div>
  );
}
